import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

const debounce = (func, delay) => {
  let timer;
  const debouncedFunc = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
  debouncedFunc.cancel = () => {
    clearTimeout(timer);
  };
  return debouncedFunc;
};

const useValueList = (params = {}, autoRefresh = false) => {
  const [valueBets, setValueBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const memoizedParams = useMemo(() => {
    const filteredParams = {};
    if (params.sport) filteredParams.sport = params.sport;
    if (params.max_odds) filteredParams.max_odds = params.max_odds;
    if (params.bookies && params.bookies.length > 0)
      filteredParams.bookies = params.bookies;
    if (params.bookie_count) filteredParams.bookie_count = params.bookie_count;
    if (params.hours_to_event)
      filteredParams.hours_to_event = params.hours_to_event;
    if (params.non_ev_mode) filteredParams.non_ev_mode = params.non_ev_mode;
    if (params.bookie_weights) filteredParams.bookie_weights = params.bookie_weights;
    return filteredParams;
  }, [
    params.sport,
    params.max_odds,
    params.bookies,
    params.bookie_count,
    params.hours_to_event,
    params.non_ev_mode,
    params.bookie_weights
  ]);

  const fetchValueBets = useCallback(
    async (isBackground = false) => {
      if (window.membershipLevel != 30) {
        return;
      }

      if (!isBackground) {
        setLoading(true);
      } else {
        setBackgroundLoading(true);
      }

      try {
        const url =
          window.location.hostname === "localhost"
            ? `./valuebets.json`
            : `${window.location.origin}/wp-json/bonusbank/v1/value-bets/`;

        const response = await axios.get(url, {
          params: memoizedParams,
          headers: {
            "content-type": "application/json",
            "X-WP-Nonce": window.nonce,
          },
        });

        const bets = Array.isArray(response.data.value_bets)
          ? response.data.value_bets
          : Object.keys(response.data.value_bets).length === 0
          ? []
          : Object.values(response.data.value_bets);

        setValueBets(bets);
        setError(null);
      } catch (err) {
        const errorDetails = {
          message:
            err.response?.data?.error?.message ||
            err.message ||
            "An unknown error occurred",
          code: err.response?.data?.error?.code || err.code || "unknown",
          details: err.response?.data?.error?.details || null,
          timestamp: new Date().toISOString(),
          requestParams: memoizedParams,
          status: err.response?.status,
        };

        console.error("Value Betting Error:", errorDetails);
        setError(errorDetails);

        if (!isBackground) {
          setValueBets([]);
        }
      } finally {
        if (!isBackground) {
          setLoading(false);
        } else {
          setBackgroundLoading(false);
        }
      }
    },
    [memoizedParams]
  );

  const debouncedFetchValueBets = useMemo(
    () => debounce(fetchValueBets, 500),
    [fetchValueBets]
  );

  // Primary effect to fetch data when params change
  useEffect(() => {
    debouncedFetchValueBets(false);
    
    return () => {
      debouncedFetchValueBets.cancel();
    };
  }, [debouncedFetchValueBets]);

  // Handle manual refresh requests
  useEffect(() => {
    if (refreshTrigger > 0) {
      debouncedFetchValueBets(true);
    }
    
    return () => {
      debouncedFetchValueBets.cancel();
    };
  }, [refreshTrigger, debouncedFetchValueBets]);

  // Handle auto-refresh
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => {
        setRefreshTrigger((prev) => prev + 1);
      }, 60000); // 1 minute in milliseconds
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh]);

  const triggerRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return {
    valueBets,
    loading,
    backgroundLoading,
    error,
    triggerRefresh,
  };
};

export default useValueList;
